<template>
  <div class="rd-screen">
    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading && !report" kind="loading"></report-state>

    <!-- Panel iz "Preuzeto": samo lista radnika, radnik se otvara kao sledeći panel -->
    <div v-else-if="embedded" class="rd-list">
      <div class="rd-stats">
        <div class="rd-pill-stat">
          <span>{{ $t("ordered") }}</span>
          <b class="rd-num">{{ stats.ordered }}</b>
        </div>
        <div class="rd-pill-stat">
          <span>{{ $t("taken") }}</span>
          <b class="rd-num">{{ stats.taken }}</b>
        </div>
        <div class="rd-pill-stat rd-pill-stat--accent">
          <span>{{ $t("difference") }}</span>
          <b class="rd-num">{{ stats.difference }}</b>
        </div>
      </div>

      <div class="rd-seg rd-seg--full" role="tablist" :aria-label="$t('order_status')">
        <button
          v-for="item in tabs"
          :key="item.key"
          type="button"
          role="tab"
          :aria-selected="String(tab === item.key)"
          @click="tab = item.key"
        >
          {{ item.label }}
        </button>
      </div>

      <div v-if="matchedWorkers.length" class="rd-card rd-rows" role="list" @keydown="onRowsKey">
        <button
          v-for="worker in matchedWorkers"
          :key="worker.id"
          type="button"
          class="rd-row"
          role="listitem"
          :aria-pressed="String(selectedId === worker.id)"
          :title="worker.source.external_code ? `${worker.name} · ${worker.source.external_code}` : worker.name"
          @click="openWorker(worker, $event)"
        >
          <span class="rd-avatar">{{ worker.initials }}</span>
          <span class="rd-row__text">
            <span class="rd-row__name">{{ worker.name }}</span>
            <span class="rd-row__meta" :title="worker.meta">{{ worker.meta }}</span>
          </span>
          <span class="rd-status" :class="`rd-status--${worker.status}`">{{ worker.statusLabel }}</span>
        </button>
      </div>
      <report-state v-else icon="mdi-account-off-outline" :title="embeddedEmptyText"></report-state>
    </div>

    <!-- Ceo spisak firme: ko je poručio, u kojoj smeni, ko je preuzeo. Klik na red otvara mesec radnika u panelu. -->
    <template v-else>
      <report-tools :copy="rows.length > 0" @copy="copyTable">
        <div class="rd-chips" role="group" :aria-label="$t('order_status')">
          <button
            v-for="chip in statusChips"
            :key="chip.value"
            type="button"
            class="rd-chip"
            :aria-pressed="String(orderStatus === chip.value)"
            @click="orderStatus = chip.value"
          >
            {{ chip.label }}
            <b class="rd-num">{{ chip.count }}</b>
          </button>
        </div>
      </report-tools>

      <report-state v-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

      <div v-else class="rd-card rd-card--scroll">
        <table class="rd-tbl rd-tbl--workers">
          <thead>
            <tr>
              <th scope="col" class="rd-col-id" :aria-sort="ariaSort('id')">
                <button type="button" @click="sortBy('id')">ID {{ sortArrow("id") }}</button>
              </th>
              <th scope="col" :aria-sort="ariaSort('name')">
                <button type="button" @click="sortBy('name')">{{ $t("worker") }} {{ sortArrow("name") }}</button>
              </th>
              <th
                v-if="isSingleDay"
                scope="col"
                class="rd-col-time"
                :title="$t('order_datetime')"
                :aria-sort="ariaSort('sortTime')"
              >
                <button type="button" @click="sortBy('sortTime')">{{ $t("time") }} {{ sortArrow("sortTime") }}</button>
              </th>
              <th
                v-for="shift in visibleShifts"
                :key="shift.id"
                scope="col"
                class="is-center"
                :aria-sort="ariaSort(shift.key)"
              >
                <button type="button" @click="sortBy(shift.key)">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                  {{ sortArrow(shift.key) }}
                </button>
              </th>
              <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
                <button type="button" @click="sortBy('ordered')">{{ $t("ordered") }} {{ sortArrow("ordered") }}</button>
              </th>
              <th scope="col" class="is-right rd-col-taken" :aria-sort="ariaSort('taken')">
                <button type="button" @click="sortBy('taken')">{{ $t("taken") }} {{ sortArrow("taken") }}</button>
              </th>
              <th scope="col" class="is-right" :aria-sort="ariaSort('difference')">
                <button type="button" @click="sortBy('difference')">
                  {{ $t("difference") }} {{ sortArrow("difference") }}
                </button>
              </th>
              <th scope="col" :aria-sort="ariaSort('stateRank')">
                <button type="button" @click="sortBy('stateRank')">{{ $t("status") }} {{ sortArrow("stateRank") }}</button>
              </th>
              <th scope="col" class="rd-col-go"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
              data-open
              tabindex="0"
              :class="{ 'is-selected': isOpen(row), 'is-idle': row.state === 'off' }"
              @click="openWorker(row, $event)"
              @keydown.enter.prevent="openWorker(row, $event)"
              @keydown.space.prevent="openWorker(row, $event)"
            >
              <td class="rd-col-id rd-num rd-muted">{{ row.id }}</td>
              <td class="is-name" :title="row.title">
                <span class="rd-person">
                  <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                  <span class="rd-person__name">{{ row.name }}</span>
                </span>
              </td>
              <td v-if="isSingleDay" class="rd-col-time rd-num rd-muted">{{ row.time || "–" }}</td>
              <td v-for="shift in visibleShifts" :key="shift.id" class="is-center">
                <span v-if="row[shift.key]" class="rs-tag rs-tag--count" :data-shift="shift.id">
                  {{ row[shift.key] }}
                  <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: shift.id }) }}</span>
                </span>
                <template v-else>
                  <span class="rd-dash" aria-hidden="true">–</span>
                  <span class="rd-visually-hidden">0</span>
                </template>
              </td>
              <td class="is-right rd-num" :class="{ 'is-zero': !row.ordered }">{{ row.ordered }}</td>
              <td class="is-right rd-num rd-col-taken" :class="{ 'is-zero': !row.taken }">{{ row.taken }}</td>
              <td
                class="is-right rd-num"
                :class="{ 'is-zero': row.difference === 0, 'is-alert': row.difference > 0 }"
              >
                {{ row.difference }}
              </td>
              <td>
                <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
              </td>
              <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="rd-col-id"></td>
              <td>{{ $t("total") }}</td>
              <td v-if="isSingleDay" class="rd-col-time"></td>
              <td v-for="shift in visibleShifts" :key="shift.id" class="is-center rd-num">{{ totals[shift.key] }}</td>
              <td class="is-right rd-num">{{ totals.ordered }}</td>
              <td class="is-right rd-num rd-col-taken">{{ totals.taken }}</td>
              <td class="is-right rd-num">{{ totals.difference }}</td>
              <td></td>
              <td class="rd-col-go"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import ReportMealsTabEmployees from "@/components/report/meals/tabs/ReportMealsTabEmployees.vue";
import moment from "moment";
import { SHIFTS } from "@/constants/shifts";
import hasWorkerList from "../../shared/hasWorkerList";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../copyTable";
import hasTableSort from "../hasTableSort";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";
import ReportDesktopWorkerMonth from "./ReportDesktopWorkerMonth.vue";

// Šta je hitnije u sortiranju po statusu: nije preuzeo → deo → nije poručio → preuzeo.
const STATE_RANK = { none: 3, part: 2, off: 1, all: 0 };

/**
 * Radnici na računaru: spisak svih radnika firme sa smenama u kojima su poručili, poručeno / preuzeto /
 * razlikom i statusom (Nije poručio, Nije preuzeo, deo, Preuzeo). Filter statusa su čipovi sa brojevima.
 * Klik na red otvara mesečni pregled radnika u panelu. U panelu (iz "Preuzeto") je samo lista radnika.
 * Nasleđuje učitavanje, filter statusa, štampu i Excel iz ReportMealsTabEmployees (štampa i Excel prate
 * izabrani status i pretragu).
 */
export default {
  name: "ReportDesktopEmployees",
  extends: ReportMealsTabEmployees,
  mixins: [hasWorkerList, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  props: {
    /** Smene iz trake: ne utiču na radnike (preuzeto se ne razlaže po smeni), samo se prihvataju */
    shifts: {
      type: Array,
      default: () => [],
    },
    /** Firma koja zamenjuje izabranu firmu iz store-a (panel iz "Preuzeto") */
    companyOverride: {
      type: Object,
      default: null,
    },
    /** Panel: samo lista radnika */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    // Štampa/Excel nasleđeni iz starog taba: sve četiri smene kao kolone.
    selectedShifts: [1, 2, 3, 4],
    selectedId: null,
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    selectedCompany() {
      return this.companyOverride || this.$store.state.report.selectedMealCompany;
    },

    // ---------------------------------------------------------- ceo spisak
    /** Čipovi statusa sa brojevima; vrednosti su isti statusi kao u starom izborniku (orderStatus). */
    statusChips() {
      const users = Array.isArray(this.report?.users) ? this.report.users : [];
      const ordered = users.filter((user) => (user.order_count || 0) > 0);
      const taken = ordered.filter((user) => (user.order_payed || 0) > 0).length;
      const counts = [users.length, ordered.length, users.length - ordered.length, taken, ordered.length - taken];
      return [
        { value: 0, label: this.$t("all") },
        { value: 1, label: this.$t("users_ordered") },
        { value: 2, label: this.$t("not_ordered") },
        { value: 3, label: this.$t("users_ordered_taken") },
        { value: 4, label: this.$t("users_ordered_not_taken") },
      ].map((chip, index) => ({ ...chip, count: counts[index] }));
    },
    /** Smene 1–3 uvek, četvrta samo kad neko u njoj ima narudžbu. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) => shift.id < 4 || this.users.some((user) => Number(user[shift.key]) > 0)
      );
    },
    allRows() {
      return this.users.map((user) => this.toRow(user));
    },
    rows() {
      const search = this.filters.search;
      const matched = search
        ? this.customSearch(
            this.allRows,
            [{ value: "name" }, { value: "idText" }, { value: "externalCode" }],
            search
          )
        : this.allRows;
      // abecedno kao druga mera: redovi sa istom vrednošću ostaju po imenu
      const byName = [...matched].sort((a, b) => a.name.localeCompare(b.name));
      return this.sortRows(byName);
    },
    totals() {
      const totals = { ordered: 0, taken: 0, difference: 0 };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = 0;
      });
      this.rows.forEach((row) => {
        totals.ordered += row.ordered;
        totals.taken += row.taken;
        totals.difference += row.difference;
        SHIFTS.forEach((shift) => {
          totals[shift.key] += row[shift.key];
        });
      });
      return totals;
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.totals.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.totals.taken },
        { key: "difference", label: this.$t("difference"), value: this.totals.difference },
      ];
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.report?.users?.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },

    // ---------------------------------------------------------- panel iz "Preuzeto"
    embeddedEmptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.workers.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  watch: {
    selectedCompany() {
      this.selectedId = null;
    },
    kpis: {
      handler(kpis) {
        this.$emit("kpis", !this.embedded && this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    /** Red tabele: radnik iz starog taba (users) + stanje narudžbe i preuzimanja. */
    toRow(user) {
      const ordered = user.order_count || 0;
      const taken = user.order_payed || 0;
      const state = ordered <= 0 ? "off" : taken <= 0 ? "none" : taken >= ordered ? "all" : "part";
      const stamp = ordered > 0 && user.date ? moment(user.date, "DD.MM.YYYY HH:mm") : null;
      const name = user.full_name.trim();
      const row = {
        id: user.id,
        idText: String(user.id),
        externalCode: user.external_code || "",
        name,
        title: user.external_code ? `${name} · ${user.external_code}` : name,
        initials: [user.name, user.lastname]
          .filter(Boolean)
          .map((part) => part.charAt(0))
          .join(""),
        time: stamp && stamp.isValid() ? stamp.format("DD.MM. HH:mm") : "",
        sortTime: stamp && stamp.isValid() ? stamp.valueOf() : 0,
        ordered,
        taken,
        difference: user.order_difference,
        state,
        stateRank: STATE_RANK[state],
        statusLabel: state === "off" ? this.$t("report_desktop.status_not_ordered") : this.statusLabel(state, taken, ordered),
        source: user,
      };
      SHIFTS.forEach((shift) => {
        row[shift.key] = Number(user[shift.key]) || 0;
      });
      return row;
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu (iz panela "Preuzeto" kao sledeći panel). */
    openWorker(worker, event) {
      this.selectedId = worker.id;
      const employee = this.toEmployeeContext(worker);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${worker.id}`,
        root: !this.embedded,
        wide: true,
        title: worker.name,
        subtitle: [`ID ${worker.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { worker: employee, compact: true },
      });
    },
    /** Strelice gore/dole prelaze između radnika u listi (panel). */
    onRowsKey(event) {
      if (!["ArrowDown", "ArrowUp"].includes(event.key)) {
        return;
      }
      const rows = [...event.currentTarget.querySelectorAll(".rd-row")];
      const index = rows.indexOf(document.activeElement);
      const next = rows[index + (event.key === "ArrowDown" ? 1 : -1)];
      if (next) {
        event.preventDefault();
        next.focus();
      }
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.isSingleDay ? [row.time] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
